import { Box, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Dispatch, bindActionCreators } from "redux";

import ProviderProfile from "@containers/ProviderProfile";
import BottomLiveBar from "../../components/BottomLiveBar";
import Header from "../../components/Header";
import MyProgress from "../../components/PatientDashboardComponents/MyProgress";
import NextAppointment from "../../components/PatientDashboardComponents/NextAppointment";
import Orders from "../../components/PatientDashboardComponents/Orders";
import ToDo from "../../components/PatientDashboardComponents/ToDo";
import { PatientDashboardData } from "../../models/PatientDashboardData";
import AuthService from "../../services/authService";
import { Appointment, Signature } from "../../services/dto/Common";
import { User } from "../../services/dto/Security";
import UserService from "../../services/userService";
import { getName } from "../../services/utils";
import { loadAppointments } from "../../store/actions/appointments";
import { updateAuthUser } from "../../store/actions/auth";
import dataAction from "../../store/actions/dataAction";
import { IAppState } from "../../store/reducers";
import { AppointmentsState } from "../../store/reducers/appointments";
import { AuthState } from "../../store/reducers/auth";
import _ from "lodash";
import { getRecordId } from "@utils/helpers";
import PolicyModal from "@containers/Modals/PolicyModal";
import RemainingTasksModal from "@containers/Modals/RemainingTasksModal";
import SignDocuments from "@containers/ESign/SignDocuments";

interface IPatientDashboardPageProps extends RouteComponentProps<any> {
  patientDashboard: PatientDashboardData;
  dataAction?: any;
}

type TaskType = "followup" | "onboarding" | "insurance" | "paymentMethod" | "esign";

const PatientDashboardPage: React.FunctionComponent<
  IPatientDashboardPageProps
> = (props) => {
  // old code
  const [dataList, setDataList] = useState<PatientDashboardData>();
  const [loading, setLoading] = useState(true);
  const [showDocumentSigning, setShowDocumentSigning] = useState(false);
  const [showRemainingTasks, setShowRemainingTasks] = useState(true);
  const { appointments, myTodo } = useSelector<IAppState, AppointmentsState>(
    (s) => s.appointments,
  );

  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [nearestApt, setNearestApt] = useState<Appointment>();
  const [delayPaperwork, setDelayPaperwork] = useState(!!sessionStorage.getItem("delayPaperwork"));
  const nearestAptRef = useRef<Appointment>();

  const [provider, setProvider] = useState<User>();
  const [therapyId, setTherapyId] = useState<string>();

  useEffect(() => {
    props.dataAction.getPatientDashboardData();
  }, [props.dataAction]);

  useEffect(() => {
    if (props.patientDashboard) {
      setDataList(props.patientDashboard || []);
    }
  }, [props.patientDashboard]);

  useEffect(() => {
    if (myTodo.data) {
      const wmTodo = myTodo.data?.items.find((i) => ["weightManagementForms", "weightManagementFollowUpForms"].includes(i.type));
      if (wmTodo) setTherapyId(wmTodo?.therapyId);
    }
  }, [myTodo.data]);

  // ---- end of old code
  const { authUser } = useSelector<IAppState, AuthState>((s) => s.auth);
  if (!authUser) return null;
  const authUserId = getRecordId(authUser);

  const dispatch = useDispatch();
  const userName = getName(authUser);

  useEffect(() => {
    if (authUser?.id) {
      dispatchAppointments();
      getRequiredSignatures();
    }
  }, [authUser?.id]);

  useEffect(() => {
    if (appointments.data) {
      const newNearestApt = getNearestAppointment(appointments.data);
      nearestAptRef.current = newNearestApt;
      setNearestApt(newNearestApt);
    }
  }, [appointments.data]);

  const dispatchAppointments = () => {
    dispatch(
      loadAppointments({
        types: ["upcoming", "ongoing"],
        patientId: authUser?.id,
      }),
    );
  };

  const getRequiredSignatures = () => {
    if (!authUserId) return setLoading(false);

    UserService.getDocumentsRequiringSignature({ patientId: authUserId })
      .then((res) => setSignatures(res.data))
      .catch((e) => console.error("Error getting required signatures", e))
      .finally(() => setLoading(false));
  };

  const checkInDateRange = (apt?: Appointment) => {
    if (!apt) return false;
    
    // Subtract seven days from the start time
    const sevenDaysBefore = moment(apt.startTime).subtract(7, "days");

    // Check if the difference is within the range of seven days
    const today = moment();
    return today.isSameOrAfter(sevenDaysBefore, "day") &&
      today.isSameOrBefore(apt.startTime, "day");
  };

  const renderModal = () => {
    if (showDocumentSigning) return;

    if (!authUser.termsConsent) {
      return (
        <PolicyModal
          userId={authUserId}
          termsConsent={!!authUser.termsConsent}
          updateAuthUser={(data: object) => {
            dispatch(updateAuthUser(data));
            AuthService.updateUserInfo(data);
          }}
        />
      );
    }

    if (showRemainingTasks) {
      return (
        <RemainingTasksModal
          remainingTasks={getRemainingTasks()}
          onClose={(dontShowAgain: boolean) => {
            if (dontShowAgain) handleDelayPaperwork();
            else setShowRemainingTasks(false);
          }}
          appointmentId={getRecordId(nearestApt)}
          therapyId={therapyId}
          esignOnSubmit={() => setShowDocumentSigning(true)}
        />
      );
    }

    return null;
  };

  const getRemainingTasks = () => {
    if (!myTodo.data) return [];
    
    const newPatientTodo = myTodo.data?.items.find((i) => i.type === "newForms");
    const followUpTodo = myTodo.data?.items.find((i) => i.type === "followUpForms");
    const weightManagementTodo = myTodo.data?.items.find((i) => i.type === "weightManagementForms");
    const weightManagementFollowUpTodo = myTodo.data?.items.find((i) => i.type === "weightManagementFollowUpForms");
    const insuranceTodo = myTodo.data?.items.find((i) => i.type === "insurance");
    const paymentMethodTodo = myTodo.data?.items.find((i) => i.type === "creditCard");

    return [
      !_.isEmpty(signatures) ? "esign" : null,
      !_.get(insuranceTodo, "completed", true) ? "insurance" : null,
      !_.get(paymentMethodTodo, "completed", true) ? "paymentMethod" : null,
      !_.get(newPatientTodo, "completed", true) ? "onboarding" : null,
      !_.get(followUpTodo, "completed", true) && checkInDateRange(nearestApt) ? "followup" : null,
      !_.get(weightManagementTodo, "completed", true) ? "weightManagement" : null,
      !_.get(weightManagementFollowUpTodo, "completed", true) ? "weightManagementFollowUp" : null,
    ].filter(Boolean) as TaskType[];
  };

  const handleDelayPaperwork = () => {
    setShowDocumentSigning(false);
    setShowRemainingTasks(false);
    setDelayPaperwork(true);

    sessionStorage.setItem("delayPaperwork", "true");
  };

  const getNearestAppointment = (aptList: any) => {
    let nearestIndex = 0;
    aptList.forEach((time: string, i: number) => {
      if (moment(time).isBefore(time[Number(i) === 0 ? i : i - 1])) {
        nearestIndex = i;
      }
    });

    return aptList[nearestIndex];
  };

  return (
    <React.Fragment>
      {!loading && !delayPaperwork && renderModal()}
      {showDocumentSigning && (
        <SignDocuments handleClose={() => {
          setShowDocumentSigning(false);
          getRequiredSignatures();
        }} />
      )}
      {!!dataList && (
        <Box sx={{ p: 1 }}>
          {provider && (
            <ProviderProfile
              bioOnly
              openBio
              provider={provider}
              handleBioClose={() => setProvider(undefined)}
            />
          )}

          <Header currentPage="Dashboard" headerType="PatientDashboard" />
          <Box
            style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {`Welcome, ${userName}!`}
            </Typography>
            {false && <MyProgress />}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: "24px",
                justifyContent: "space-between",
              }}
            >
              <NextAppointment
                onOpenProviderInfo={setProvider}
                patientId={authUser?.id}
              />
              <ToDo />
              <Orders />
              {/* <div className="col col-md-4 col-xs-12">
                <WatchAndLearn dataList={dataList.watchAndLearn} />
              </div> */}
            </Box>

            <div className="row">
              {/* <div className="col col-md-4 col-xs-12">
                <Snewzz />
              </div>
              <div className="col col-md-4 col-xs-12">
                <AppStoreBanner dataList={dataList.appStoreBanner} />
              </div> */}
            </div>

            <BottomLiveBar />
          </Box>
        </Box>
      )}
    </React.Fragment>
  );
};

const mapStateToProps = (state: any) => ({ ...state.dataReducer });

const matchDispatchToProps = (dispatch: Dispatch) => ({
  actions: bindActionCreators({}, dispatch),
  dataAction: bindActionCreators({ ...dataAction }, dispatch),
});

export default connect(
  mapStateToProps,
  matchDispatchToProps,
)(withRouter(PatientDashboardPage));
